import React, { useCallback, useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Alert, Card, Flex, Spin } from 'antd'
import { useFetchConfidenceDistributionAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import { useFetchQuestionsQuery } from '../../../../../../../store/apis/question.api'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import { buildAssignmentPath } from '../../../../../../../utils/link.helper'
import { useVersionId } from '../../../../../../../utils/parseURL.helper'
import {
    ConfidenceLevels,
    getConfidenceLevelColor,
    getConfidenceLevelNoun,
} from '../../../../../../../constants/confidence-levels'
import { useNavigate } from 'react-router-dom'
import { flatten } from '../../../../../../../utils/array.helper'

const BAR_HEIGHT = 32

const ConfidenceDistributionChart = ({ token }) => {
    const navigate = useNavigate()
    const versionId = useVersionId()

    const {
        data: analytics,
        isFetching: isFetchingAnalytics,
        error,
    } = useFetchConfidenceDistributionAnalyticsQuery({ token })

    let { data: questions, isFetching: isFetchingQuestions } = useFetchQuestionsQuery({ token, versionId })
    questions = flatten(questions, { excludeParents: true })

    const isFetching = isFetchingAnalytics || isFetchingQuestions

    const data = useMemo(() => {
        if (!analytics || !questions) return []

        return questions
            .map(question => {
                const analyticsQuestion = analytics.questions.find(q => q.id === question.id)
                if (!analyticsQuestion) return null

                const result = {
                    name: question.name,
                    questionId: question.id,
                    totalSubmissions: analyticsQuestion.totalSubmissionsPerQuestion,
                }

                // Add data for each confidence level
                analyticsQuestion.confidenceLevels.forEach(level => {
                    result[`confidence_${level.level}`] = level.percentFromQuestionSubmissions
                    result[`confidence_${level.level}_count`] = level.numberOfSubmissions
                })

                return result
            })
            .filter(Boolean)
    }, [analytics, questions])

    const chartHeight = Math.max(160, (questions?.length || 0) * BAR_HEIGHT + 100)

    const handleBarClick = data => {
        if (data && data.questionId) navigate(buildAssignmentPath(token, data.questionId))
    }

    const renderCustomYAxisTick = useCallback(props => {
        const { x, y, payload } = props
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={0} textAnchor='end' fill='#666' style={{ fontSize: 12 }}>
                    {payload.value.length > 20 ? `${payload.value.substring(0, 20)}...` : payload.value}
                </text>
            </g>
        )
    }, [])

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null

        const question = questions.find(question => question.name === label)
        const questionData = data.find(d => d.name === label)

        const confidenceLevels = payload.map(entry => ({
            level: parseInt(entry.name.split('_')[1]),
            value: entry.value,
            count: questionData[`${entry.name}_count`],
            color: getConfidenceLevelColor(parseInt(entry.name.split('_')[1])),
        }))

        return (
            <Card size='small' className='min-w-[300px]'>
                <Title level={5} style={{ margin: '0 0 8px 0', fontSize: '0.9rem' }}>
                    {question.name}
                </Title>
                <Flex vertical gap={8}>
                    {confidenceLevels.map((item, index) => (
                        <Flex key={index} justify='space-between' align='center' gap={8}>
                            <Text style={{ color: getConfidenceLevelColor(item.level) }}>
                                {getConfidenceLevelNoun(item.level)}
                            </Text>
                            <Text>{`${item.value}% - ${item.count} submissions`}</Text>
                        </Flex>
                    ))}
                    <Text type='secondary'>Total submissions: {questionData.totalSubmissions}</Text>
                </Flex>
            </Card>
        )
    }

    if (error) return <Alert message={error} type='error' showIcon />

    return (
        <Spin spinning={isFetching} tip='Loading...'>
            <ResponsiveContainer width='100%' height={chartHeight}>
                <BarChart data={data} layout='vertical' margin={{ top: 0, right: 12, left: 12, bottom: 36 }}>
                    <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                    <XAxis
                        type='number'
                        tickFormatter={value => `${value}%`}
                        label={{
                            value: 'Percent of Submissions',
                            position: 'bottom',
                            offset: 15,
                        }}
                        domain={[0, 100]}
                    />
                    <YAxis
                        dataKey='name'
                        type='category'
                        tick={renderCustomYAxisTick}
                        width={144}
                        label={{
                            value: 'Questions',
                            angle: -90,
                            position: 'left',
                        }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {Object.values(ConfidenceLevels).map(level => (
                        <Bar
                            key={`confidence_${level}`}
                            dataKey={`confidence_${level}`}
                            stackId='confidence'
                            fill={getConfidenceLevelColor(level)}
                            name={`confidence_${level}`}
                            onClick={handleBarClick}
                            style={{ cursor: 'pointer' }}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </Spin>
    )
}

export default ConfidenceDistributionChart
